import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackEmptyListingSuggestionsClick, trackEmptyListingSuggestionsShow } from './tracking';
import { loadEmptyListingSuggestions } from '../../../common/actions/emptyListingSuggestions';
import { ListingSkeleton } from '../../../common/components/OfferCardHorizontal/components/OfferCardSkeleton';
import { prepareEmptyListingSuggestionOffer } from '../../../common/mappers/emptyListingSuggestions';
import { IDailyRentOfferForMobileSite } from '../../../common/repositories/dailyrent/entities/daily_rent_offers_for_web/DailyRentOfferForMobileSite';
import { selectIsDesktopListingVisible } from '../../../common/selectors/desktopListing';
import { selectEmptyListingSuggestions } from '../../../common/selectors/emptyListingSuggestions';
import { selectResultsJsonQuery } from '../../../common/selectors/results';
import { EFetchStatus } from '../../../common/types/listingOffers';
import { TThunkDispatch } from '../../../common/types/redux';
import { EmptyListingSuggestionsLayout } from '../../components/EmptyListingSuggestionsLayout';
import { OfferCardDesktopContainer } from '../OfferCardDesktopContainer';

const SKELETONS_COUNT = 2;

export const EmptyListingSuggestionsContainer = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const isSidebarVisible = useSelector(selectIsDesktopListingVisible);
  const { status, offers } = useSelector(selectEmptyListingSuggestions);
  const jsonQuery = useSelector(selectResultsJsonQuery);
  const hasTrackedShowRef = React.useRef(false);
  const waitForFreshLoadRef = React.useRef(false);

  const cardOffers = React.useMemo(() => offers.map(prepareEmptyListingSuggestionOffer), [offers]);

  const isLoading = status === EFetchStatus.LOADING;
  const hasOffers = cardOffers.length > 0;

  React.useEffect(() => {
    if (!isSidebarVisible) {
      hasTrackedShowRef.current = false;
      waitForFreshLoadRef.current = true;

      return;
    }

    dispatch(loadEmptyListingSuggestions());
  }, [isSidebarVisible, dispatch]);

  React.useEffect(() => {
    if (status === EFetchStatus.LOADING || status === null) {
      hasTrackedShowRef.current = false;
      waitForFreshLoadRef.current = false;
    }
  }, [status]);

  React.useEffect(() => {
    if (!isSidebarVisible || !hasOffers || hasTrackedShowRef.current || waitForFreshLoadRef.current) {
      return;
    }

    hasTrackedShowRef.current = true;
    trackEmptyListingSuggestionsShow({ offers, jsonQuery });
  }, [hasOffers, isSidebarVisible, jsonQuery, offers]);

  const handleCardClick = (offer: IDailyRentOfferForMobileSite, position: number) => {
    trackEmptyListingSuggestionsClick({
      offer,
      position,
      jsonQuery,
    });
  };

  if (!isSidebarVisible || (!isLoading && !hasOffers)) {
    return null;
  }

  return (
    <EmptyListingSuggestionsLayout>
      {isLoading ? (
        <ListingSkeleton count={SKELETONS_COUNT} />
      ) : (
        cardOffers.map((offer, index) => (
          <OfferCardDesktopContainer
            key={offer.id}
            offer={offer}
            offerPosition={index + 1}
            offersLength={cardOffers.length}
            variant="recommendation"
            onCardClick={() => handleCardClick(offer, index + 1)}
          />
        ))
      )}
    </EmptyListingSuggestionsLayout>
  );
};
